import { ChannelGroupType, QueueMediaType } from '@dxp/core/types';
import { toPercentage } from '@dxp/core/helpers';
import { IQueue, IQueueStatistics } from '../queue/queue';
import { ITelXLKpiQueueThreshold } from '../rbac/queue-kpi';
import { IValueWithMetric, IMetric } from '../metrics/metric';

export const NBU_ID = 'nobussinessunit';

export interface IBusinessUnit {
    id: string;
    name: string;
    kpiTelXLThreshold: ITelXLKpiQueueThreshold;
    queues: IQueue[];
    activeQueues: IQueue[];
    hasActivity: boolean;
    configuredQueues: IQueue[];
    hasConfigured: boolean;
    channelGroups: ChannelGroupType[];
    mediaTypes: QueueMediaType[];

    visibleByChannelFilter(filter: ChannelGroupType | undefined): boolean;
    statistics(index?: string): IBusinessUnitStatistics;
}

export interface IBusinessUnitStatistics {
    answeredWithinSLAPercentage: IValueWithMetric;
    answeredWithinSLAPercentageMetricByQueue: IMetric[];
    waiting: IValueWithMetric;
    connected: number;
    success: number;
    complete: number;
    timedOut: number;
    abandoned: number;
    outOfSla: number;
    currentLongestWaitingTimeInSeconds: IValueWithMetric;
    longestWaitingTimeInSeconds: IValueWithMetric;
    avgWaitingTimeInSeconds: IValueWithMetric;
    avgHandlingTimeInSeconds: IValueWithMetric;
}

export class BusinessUnit implements IBusinessUnit {
    readonly id: string;
    readonly name: string;
    readonly #queues: IQueue[];
    readonly #kpiTelXLThreshold: ITelXLKpiQueueThreshold;

    constructor(
        id: string,
        name: string,
        queues: IQueue[],
        kpiTelXLThreshold: ITelXLKpiQueueThreshold,
    ) {
        this.id = id;
        this.name = name;
        this.#queues = queues;
        this.#kpiTelXLThreshold = kpiTelXLThreshold;
    }

    get hasActivity(): boolean {
        return this.activeQueues.length > 0;
    }

    get hasConfigured(): boolean {
        return this.configuredQueues.length > 0;
    }

    get queues(): IQueue[] {
        return this.#queues;
    }

    get configuredQueues(): IQueue[] {
        return this.#queues.filter(q => q.isConfigured);
    }

    get activeQueues(): IQueue[] {
        return this.#queues.filter(q => q.hasActivity);
    }

    get kpiTelXLThreshold(): ITelXLKpiQueueThreshold {
        return this.#kpiTelXLThreshold;
    }

    get channelGroups(): ChannelGroupType[] {
        return [
            ...new Set(this.configuredQueues.flatMap(q => q.channelGroups)),
        ];
    }

    get mediaTypes(): QueueMediaType[] {
        return [...new Set(this.configuredQueues.flatMap(q => q.mediaType))];
    }

    visibleByChannelFilter(filter: ChannelGroupType | undefined): boolean {
        return this.#queues.some(q => q.visibleByChannelFilter(filter));
    }

    statistics(index?: ChannelGroupType): IBusinessUnitStatistics {
        if (index === undefined) {
            return this.#calculatePerformance(
                this.activeQueues.map(q => q.statistics()),
            );
        }

        return this.#calculatePerformance(
            this.activeQueues.map(q => q.statistics(index)),
        );
    }

    #calculatePerformance(
        queueStatistics: IQueueStatistics[],
    ): IBusinessUnitStatistics {
        if (queueStatistics.length === 0) {
            return businessUnitStatisticDefault();
        }

        const answeredWithinSLAPercentageMetricByQueue = queueStatistics.map(
            qs => qs.answeredWithinSLAPercentage.metric,
        );

        const answeredWithinSLAPercentage = Math.round(
            queueStatistics.reduce((total: number, qs: IQueueStatistics) => {
                return total + qs.answeredWithinSLAPercentage.value;
            }, 0) / queueStatistics.length,
        );

        const waiting = queueStatistics.reduce(
            (total: number, qs: IQueueStatistics) => {
                return total + qs.waiting.value;
            },
            0,
        );

        const connected = queueStatistics.reduce(
            (total: number, qs: IQueueStatistics) => {
                return total + qs.connected;
            },
            0,
        );

        const success = queueStatistics.reduce(
            (total: number, qs: IQueueStatistics) => {
                return total + qs.success;
            },
            0,
        );

        const complete = queueStatistics.reduce(
            (total: number, qs: IQueueStatistics) => {
                return total + qs.complete;
            },
            0,
        );

        const timedOut = queueStatistics.reduce(
            (total: number, qs: IQueueStatistics) => {
                return total + qs.timedOut;
            },
            0,
        );

        const abandoned = queueStatistics.reduce(
            (total: number, qs: IQueueStatistics) => {
                return total + qs.abandoned;
            },
            0,
        );

        const outOfSla = queueStatistics.reduce(
            (total: number, qs: IQueueStatistics) => {
                return total + qs.outOfSla;
            },
            0,
        );

        const avgHandlingTimeQueues = queueStatistics.filter(
            qs => qs.avgHandlingTimeInSeconds.value > 0,
        ).length;

        const averageHandlingTimeInSeconds =
            avgHandlingTimeQueues > 0
                ? queueStatistics.reduce(
                      (total: number, qs: IQueueStatistics) => {
                          return total + qs.avgHandlingTimeInSeconds.value;
                      },
                      0,
                  ) / avgHandlingTimeQueues
                : 0;

        const longestWaitingTimeInSeconds = Math.max(
            ...queueStatistics.map(qs => qs.longestWaitingTimeInSeconds.value),
        );

        const avgWaitingTimeInSeconds = Math.max(
            ...queueStatistics.map(qs => qs.avgWaitingTimeInSeconds.value),
        );

        const currentLongestWaitingTimeStamp = Math.max(
            ...queueStatistics.map(
                qs => qs.currentLongestWaitingTimeInSeconds.value,
            ),
        );

        return {
            answeredWithinSLAPercentageMetricByQueue:
                answeredWithinSLAPercentageMetricByQueue,
            answeredWithinSLAPercentage: {
                value: answeredWithinSLAPercentage,
                metric: {
                    percent: answeredWithinSLAPercentage,
                    states: this.kpiTelXLThreshold.slaThreshold.enabled
                        ? [
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold.slaThreshold.low,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold.slaThreshold.high,
                                  ),
                                  state: 'success',
                              },
                          ]
                        : [],
                },
            },
            waiting: {
                value: waiting,
                metric: this.kpiTelXLThreshold.maxConversationsWaiting.enabled
                    ? {
                          percent: toPercentage(
                              waiting /
                                  this.kpiTelXLThreshold.maxConversationsWaiting
                                      .high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold
                                          .maxConversationsWaiting.low /
                                          this.kpiTelXLThreshold
                                              .maxConversationsWaiting.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            connected: connected,
            success: success,
            complete: complete,
            currentLongestWaitingTimeInSeconds: {
                value: currentLongestWaitingTimeStamp,
                metric: this.#kpiTelXLThreshold.maxLongestWaitTimeSeconds
                    .enabled
                    ? {
                          percent: this.#kpiTelXLThreshold
                              .maxLongestWaitTimeSeconds.high
                              ? toPercentage(
                                    currentLongestWaitingTimeStamp /
                                        this.#kpiTelXLThreshold
                                            .maxLongestWaitTimeSeconds.high,
                                )
                              : 0,
                          states: [
                              {
                                  value: toPercentage(
                                      this.#kpiTelXLThreshold
                                          .maxLongestWaitTimeSeconds.low /
                                          this.#kpiTelXLThreshold
                                              .maxLongestWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            longestWaitingTimeInSeconds: {
                value: longestWaitingTimeInSeconds,
                metric: this.kpiTelXLThreshold.maxLongestWaitTimeSeconds.enabled
                    ? {
                          percent: toPercentage(
                              longestWaitingTimeInSeconds /
                                  this.kpiTelXLThreshold
                                      .maxLongestWaitTimeSeconds.high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold
                                          .maxLongestWaitTimeSeconds.low /
                                          this.kpiTelXLThreshold
                                              .maxLongestWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            avgWaitingTimeInSeconds: {
                value: avgWaitingTimeInSeconds,
                metric: this.kpiTelXLThreshold.averageWaitTimeSeconds.enabled
                    ? {
                          percent: toPercentage(
                              (avgWaitingTimeInSeconds ?? 0) /
                                  this.kpiTelXLThreshold.averageWaitTimeSeconds
                                      .high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold
                                          .averageWaitTimeSeconds.low /
                                          this.kpiTelXLThreshold
                                              .averageWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            timedOut: timedOut,
            abandoned: abandoned,
            outOfSla: outOfSla,
            avgHandlingTimeInSeconds: {
                value: averageHandlingTimeInSeconds,
                metric:
                    this.#kpiTelXLThreshold.averageHandlingTimeSeconds &&
                    this.#kpiTelXLThreshold.averageHandlingTimeSeconds.enabled
                        ? {
                              percent: toPercentage(
                                  averageHandlingTimeInSeconds /
                                      this.#kpiTelXLThreshold
                                          .averageHandlingTimeSeconds.target,
                              ),
                              states: [
                                  {
                                      value: toPercentage(
                                          this.#kpiTelXLThreshold
                                              .averageHandlingTimeSeconds.low /
                                              this.#kpiTelXLThreshold
                                                  .averageHandlingTimeSeconds
                                                  .target,
                                      ),
                                      state: 'warning',
                                  },
                                  {
                                      value: toPercentage(
                                          this.#kpiTelXLThreshold
                                              .averageHandlingTimeSeconds.high /
                                              this.#kpiTelXLThreshold
                                                  .averageHandlingTimeSeconds
                                                  .target,
                                      ),
                                      state: 'danger',
                                  },
                              ],
                          }
                        : { percent: 0, states: [] },
            },
        };
    }
}

export const businessUnitStatisticDefault = (): IBusinessUnitStatistics => {
    return {
        answeredWithinSLAPercentageMetricByQueue: [],
        answeredWithinSLAPercentage: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        waiting: { value: 0, metric: { percent: 0, states: [] } },
        connected: 0,
        success: 0,
        complete: 0,
        timedOut: 0,
        abandoned: 0,
        outOfSla: 0,
        longestWaitingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        avgWaitingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        currentLongestWaitingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        avgHandlingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
    };
};
